import { motion } from 'framer-motion';
import { Award, Users, Target, TrendingUp } from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';

const About = () => {
  const milestones = [
    { year: '2016', event: 'EWSS Automation Founded', description: 'Established with a mission to deliver world-class automation solutions' },
    { year: '2018', event: 'BMS Expertise', description: 'Became leaders in Building Management Systems and energy management' },
    { year: '2020', event: 'Industrial Expansion', description: 'Expanded capabilities into large-scale industrial automation projects' },
    { year: '2025', event: 'Distribution Launch', description: 'Launched wholesale distribution division for premium automation components' }
  ];

  const values = [
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Excellence',
      description: 'Committed to delivering the highest quality automation solutions'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Partnership',
      description: 'Building long-term relationships with clients and suppliers'
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Innovation',
      description: 'Staying ahead with cutting-edge automation technologies'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Growth',
      description: 'Continuously evolving to meet industry demands'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-ewss-navy via-ewss-navy to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              <span className="text-ewss-cyan">30+ Years</span> of Combined Experience
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Delivering world-class automation solutions since 2016, backed by decades of industry expertise
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-ewss-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <div className="aspect-square rounded-xl overflow-hidden border border-ewss-cyan/30 bg-black/20">
                <img 
                  src="https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/yd4rzz8r_smart%20iot%20industrial%20automation%20with%20trends%20and%20graphs.jpg"
                  alt="Smart IoT Industrial Automation"
                  className="w-full h-full object-cover"
                />
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Our <span className="text-ewss-cyan">Story</span>
              </h2>
              <div className="space-y-4 text-gray-300">
                <p>
                  Founded in 2016, EWSS Automation was established with a clear mission: to deliver world-class automation solutions to South Africa's building and industrial sectors.
                </p>
                <p>
                  With over three decades of combined industry experience, we have been at the forefront of automation technology, providing advanced management and control solutions to commercial, private, and public sector clients. Our cloud-based platforms enable seamless system integration and real-time data visibility, while our experienced engineering team delivers expert consulting services to support organisations in achieving their operational efficiency and energy management goals.
                </p>
                <p>
                  To date, we have successfully completed more than 100 projects, ranging from small-scale Building Management System (BMS) installations to large industrial control system deployments.
                </p>
                <p className="text-ewss-cyan font-semibold">
                  Today, EWSS Automation is expanding beyond service delivery to become a trusted wholesale distribution partner. By combining deep engineering expertise with direct access to premium automation components from leading global manufacturers, we are uniquely positioned to support integrators, contractors, and end users with reliable, high-performance solutions.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-gradient-to-b from-ewss-navy to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="text-4xl md:text-5xl font-bold text-center text-white mb-16">
              Our <span className="text-ewss-cyan">Journey</span>
            </h2>
          </AnimatedSection>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-ewss-cyan/30 hidden md:block" />

            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <AnimatedSection key={index} delay={index * 0.1}>
                  <div className={`flex flex-col md:flex-row items-center gap-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}>
                    <div className="flex-1 text-center md:text-right">
                      {index % 2 === 0 && (
                        <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-ewss-cyan/30">
                          <div className="text-3xl font-bold text-ewss-cyan mb-2">{milestone.year}</div>
                          <h3 className="text-xl font-bold text-white mb-2">{milestone.event}</h3>
                          <p className="text-gray-400">{milestone.description}</p>
                        </div>
                      )}
                    </div>

                    <div className="relative z-10">
                      <div className="w-16 h-16 bg-ewss-cyan rounded-full flex items-center justify-center border-4 border-ewss-navy">
                        <div className="w-8 h-8 bg-ewss-navy rounded-full" />
                      </div>
                    </div>

                    <div className="flex-1 text-center md:text-left">
                      {index % 2 !== 0 && (
                        <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-ewss-cyan/30">
                          <div className="text-3xl font-bold text-ewss-cyan mb-2">{milestone.year}</div>
                          <h3 className="text-xl font-bold text-white mb-2">{milestone.event}</h3>
                          <p className="text-gray-400">{milestone.description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-ewss-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="text-4xl md:text-5xl font-bold text-center text-white mb-4">
              Our <span className="text-ewss-cyan">Values</span>
            </h2>
            <p className="text-center text-gray-400 text-lg mb-12 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div className="p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-ewss-cyan/20 hover:border-ewss-cyan/50 transition-all duration-300 group hover:shadow-[0_0_20px_rgba(0,229,255,0.2)] text-center h-full">
                  <div className="w-16 h-16 bg-ewss-cyan/10 rounded-full flex items-center justify-center text-ewss-cyan mb-4 mx-auto group-hover:scale-110 transition-transform">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                  <p className="text-gray-400 text-sm">{value.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;